import {
  useLazyGetTourSearchLinksResultsQuery,
  useRestoreTourMutation,
} from '@carboncopyco/data';
import {
  ArchiveIcon,
  CopyOutlineIcon,
  DuplicateOutlineIcon,
  EditIcon,
  MoreVerticalOutlineIcon,
  RightOutlineIcon,
  ShareOutlineIcon,
  ShowOutlineIcon,
  SubdirectoryRightOutlineIcon,
} from '@carboncopyco/icons';
import {
  PermissionAction,
  PermissionKey,
  type Tour,
} from '@carboncopyco/types';
import {
  Button,
  Dropdown,
  DropdownItem,
  InlineLink,
  SubMenu,
  TextCaption,
  Tooltip,
  Typography,
  notify,
} from '@carboncopyco/ui';
import { useTourAnalytics } from '@carboncopyco/utils';
import NiceModal from '@ebay/nice-modal-react';
import { useBoolean } from 'ahooks';
import { type ReactNode, isValidElement, useRef } from 'react';
import {
  CopyTourModalWithFolders,
  DuplicateTourModal,
  MoveTourWithFoldersModal,
} from '../../../../AssetActions/Tour';
import { ArchiveTourModal } from '../../../../AssetActions/Tour/modals/ArchiveTourModal';
import { CreateShareTourFlow } from '../../../../Share/ShareTourFlow';
import { usePermissionsContext } from '../../../../utils';
import { useWorkspacePageProps } from '../../WorkspacePageContext';
import { ArchiveTileDropdown } from '../ArchiveTileDropdown';

type TourActionsDropdownProps = {
  tour: Tour;
  archived?: boolean;
  inFolder?: boolean;
  handleMenuOpen: (isOpen: boolean) => void;
  triggerElement?: ReactNode;
};

export const TourActionsDropdown = ({
  tour,
  archived,
  inFolder,
  handleMenuOpen,
  triggerElement,
}: TourActionsDropdownProps) => {
  const [isMenuOpen, { toggle: toggleMenu }] = useBoolean(false);
  const archiveMessageRef = useRef<HTMLDivElement>(null);
  const [showDuplicateTourModal, setShowDuplicateTourModal] = useBoolean();
  const [showArchiveTourModal, setShowArchiveTourModal] = useBoolean();
  const shareInternallyRef = useRef<HTMLDivElement>();

  const {
    trackTourClickPreview,
    trackTourClickDuplicate,
    trackTourClickMoveToWorkspace,
    trackTourClickArchive,
    trackTourClickCopyToWorkspace,
  } = useTourAnalytics({
    location: 'tour_info_page_actions_menu',
    tourId: tour.id,
  });
  const {
    organizationId,
    workspaceId,
    goToTourPreview,
    handleEditTour,
    goToTourShares,
    toursShareAspectRatioEnabled,
  } = useWorkspacePageProps();
  const [restoreTour] = useRestoreTourMutation();
  const [getLinkResponse, { data: linksResponse }] =
    useLazyGetTourSearchLinksResultsQuery();
  const { getPermissions } = usePermissionsContext();

  const updateTourPermission = getPermissions(
    PermissionKey.tour,
    PermissionAction.updateTourById
  );
  const copyTourPermission = getPermissions(
    PermissionKey.tour,
    PermissionAction.copyTourToWorkspace
  );
  const duplicateTourPermission = getPermissions(
    PermissionKey.tour,
    PermissionAction.duplicateTour
  );
  const moveTourPermission = getPermissions(
    PermissionKey.tour,
    PermissionAction.moveTourToWorkspace
  );
  const restoreTourPermission = getPermissions(
    PermissionKey.tour,
    PermissionAction.restoreATour
  );

  return (
    <div onClick={(event) => event.stopPropagation()}>
      {archived ? (
        <ArchiveTileDropdown
          inFolder={inFolder}
          restore={() => {
            restoreTour({ id: tour.id, organizationId })
              .unwrap()
              .then(() => {
                notify.success(
                  `Tour "${tour.tourName}" was successfully restored`
                );
              })
              .catch(() => {
                notify.error(`Error restoring tour "${tour.tourName}"`);
              });
          }}
          preview={() => {
            toggleMenu();
            goToTourPreview(tour.id);
          }}
          open={isMenuOpen}
          toggleOpen={() => {
            handleMenuOpen(!isMenuOpen);
            toggleMenu();
          }}
        />
      ) : (
        <Dropdown
          asChild
          width={200}
          open={isMenuOpen}
          onOpenChange={() => {
            handleMenuOpen(!isMenuOpen);
            !isMenuOpen &&
              getLinkResponse({
                workspaces: [workspaceId],
                organizationId,
                id: String(tour.id),
                active: true,
              });
            toggleMenu();
          }}
          triggerElement={
            isValidElement(triggerElement) ? (
              triggerElement
            ) : (
              <Button
                aria-label="tour tile actions"
                small
                dark
                icon={<MoreVerticalOutlineIcon />}
              />
            )
          }
        >
          <div className="p-2 flex flex-col gap-2">
            <div>
              <DropdownItem
                iconLeft={<ShowOutlineIcon size={20} />}
                label="Preview"
                onClick={(event) => {
                  event.preventDefault();
                  trackTourClickPreview();
                  toggleMenu();
                  handleMenuOpen(isMenuOpen);
                  goToTourPreview(tour.id);
                }}
              />
              {!tour.readOnly && updateTourPermission && (
                <DropdownItem
                  iconLeft={<EditIcon />}
                  label="Edit"
                  onClick={(event) => {
                    event.preventDefault();
                    toggleMenu();
                    handleMenuOpen(isMenuOpen);
                    handleEditTour(tour.id, tour.folderId);
                  }}
                />
              )}
              {!tour.readOnly && duplicateTourPermission && (
                <DropdownItem
                  iconLeft={<DuplicateOutlineIcon size={20} />}
                  label="Duplicate"
                  onClick={() => {
                    trackTourClickDuplicate();
                    setShowDuplicateTourModal.setTrue();
                  }}
                />
              )}
              {!tour.readOnly && copyTourPermission && (
                <DropdownItem
                  iconLeft={<CopyOutlineIcon size={20} />}
                  label="Copy to..."
                  onClick={() => {
                    trackTourClickCopyToWorkspace();
                    NiceModal.show(CopyTourModalWithFolders, {
                      tour,
                      organizationId,
                    });
                  }}
                />
              )}
              {!tour.readOnly && moveTourPermission && (
                <DropdownItem
                  iconLeft={<SubdirectoryRightOutlineIcon size={18} />}
                  label="Move to..."
                  onClick={() => {
                    trackTourClickMoveToWorkspace();
                    NiceModal.show(MoveTourWithFoldersModal, {
                      tour,
                      organizationId,
                      workspaceId: tour?.workspaceId,
                    });
                  }}
                />
              )}
              <SubMenu
                width={205}
                triggerProps={{
                  label: 'Share',
                  iconRight: <RightOutlineIcon />,
                  iconLeft: <ShareOutlineIcon size={18} />,
                }}
              >
                <div ref={shareInternallyRef}>
                  <DropdownItem
                    label="Share internally"
                    disabled
                  />
                </div>
                <Tooltip
                  noArrow
                  placement="top-end"
                  anchorRef={shareInternallyRef}
                >
                  <div className="p-3">
                    <Typography
                      fontSize="text-ds-200"
                      lineHeight="leading-ds-200"
                      color="text-ds-natural"
                      fontWeight="font-ds-normal"
                      letterSpacing="tracking-ds-tight"
                      label="Tours can only be shared externally"
                    />
                  </div>
                </Tooltip>

                <DropdownItem
                  label="Share externally"
                  onClick={() => {
                    NiceModal.show(CreateShareTourFlow, {
                      workspaceId,
                      toursShareAspectRatioEnabled,
                      organizationId,
                      tourId: tour.id,
                      goToShares: () => goToTourShares(tour.id),
                    });
                  }}
                />
              </SubMenu>

              {!tour.readOnly && restoreTourPermission && (
                <>
                  <div ref={archiveMessageRef}>
                    <DropdownItem
                      disabled={
                        !linksResponse || !!linksResponse?.metadata?.totalItems
                      }
                      iconLeft={<ArchiveIcon size={20} />}
                      label="Archive"
                      onClick={() => {
                        trackTourClickArchive();
                        setShowArchiveTourModal.setTrue();
                      }}
                    />
                  </div>
                  {!!linksResponse?.metadata?.totalItems && (
                    <Tooltip
                      interactive
                      anchorRef={archiveMessageRef}
                      placement="bottom"
                      offset={[0, 20]}
                    >
                      <div className="flex flex-col items-start p-2.5">
                        <TextCaption
                          label="To archive this tour, please deactivate all of its shared links first."
                          color="text-ds-natural-600"
                        />
                        <InlineLink
                          as="div"
                          onClick={() => goToTourShares(tour.id)}
                          label="View shares"
                        />
                      </div>
                    </Tooltip>
                  )}
                </>
              )}
            </div>
          </div>
        </Dropdown>
      )}
      {showDuplicateTourModal && (
        <DuplicateTourModal
          onClose={setShowDuplicateTourModal.setFalse}
          show={showDuplicateTourModal}
          organizationId={organizationId}
          tourId={tour.id}
        />
      )}
      {showArchiveTourModal && (
        <ArchiveTourModal
          folderId={String(tour.folderId)}
          tourId={tour.id}
          organizationId={Number(organizationId)}
          show={showArchiveTourModal}
          onClose={setShowArchiveTourModal.setFalse}
        />
      )}
    </div>
  );
};
